<template>
    <div class="flex w-full md:flex-row-reverse md:flex-nowrap md:justify-end">
        <div
            v-if="screen.minMD && !post.postDetails.is_nsfw"
            class="hidden ml-auto md:flex z-1"
        >
            <BtnMenuShare
                data-testid="post-header_btn-menu-share"
                :share-title="
                    t('share.title', {
                        name: contextData.name,
                    })
                "
                :share-url="shareUrl"
                size="sm"
                variant="tonal"
                color="primary"
                color-on-touch
            />
            <BtnMenu
                color-on-touch
                variant="tonal"
                color="primary"
                size="sm"
                left-icon="more"
                :options="conditionalMenuItems"
                menu-placement="bottom-end"
            />
        </div>
        <UserAvatar
            :name="user ? user.name : anonymousUser.name"
            :user-id="user ? user.id : 0"
            :link="user ? user.profileUrl : null"
            :karma-points="user && user.id ? user.karmaPoints : null"
            show-details
            :anonymous-user="userPreview"
            :anonymous-role="userPreview ? userPreview.user_role : null"
            :is-anonymous-user="post.postDetails.isAnonymous"
            :picture="user ? user.profilePictureUrl : `/${anonymousUser.icon}`"
            :animated-avatar="user ? user.gamify_avatar_url : null"
            :timestamp="post.postDetails.editDateHuman"
            :show-admin-badge="user ? user.hasAdminBadge : false"
            class="post-header-user-avatar md:mr-2 flex-grow min-w-0 max-w-full exclude-verified exclude-auth"
            size="default"
        >
            <template #labels>
                <Tag
                    v-if="post.postDetails.isAnonymous && post.isOwner"
                    color-class="bg-slate-400 text-white"
                    class="ml-1"
                    :text="t('post:labels:you')"
                    :size="TagSize.SM"
                ></Tag>
            </template>
            <template #context>
                <TagContainer
                    v-if="post.isPinned"
                    :title="t('post:pinnedPost')"
                    disabled
                    context="post"
                    icon="icon-pin"
                    icon-size="sm"
                    weight="normal"
                    class="tag flex-shrink-0"
                ></TagContainer>
                <template v-if="shouldShowContextData">
                    <TagContainer
                        v-if="shouldShowExtraTag"
                        :title="post.course.name"
                        :url="
                            route('course.show', {
                                courseName: post.course.slug,
                                id: post.course.id,
                            })
                        "
                        context="post"
                        icon="icon-course"
                        icon-size="sm"
                        class="tag restricted truncate"
                    ></TagContainer>

                    <span
                        v-if="isDocumentContext"
                        class="hidden opacity-50 md:inline mr-1"
                        >{{ t('post:in') }}</span
                    >
                    <TagContainer
                        :title="contextData.name"
                        :url="urlToContextPage"
                        :icon="selectedIcon"
                        :class="['tag truncate', { restricted: isDocumentContext }]"
                        context="post"
                        icon-size="sm"
                    ></TagContainer>
                </template>
            </template>
        </UserAvatar>
    </div>
</template>

<script>
import route from '@router/route';
import TagContainer from '@components/TagContainer';
import BtnMenuShare from '@components/BtnMenuShare';
import { mapGetters } from 'vuex';
import stripWhiteSpaceSpecialCharacters from '@/helpers/stripWhiteSpaceSpecialCharacters';
import { TagSize } from '@components/Tag';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'PostHeader',
    components: {
        TagContainer,
        BtnMenuShare,
    },
    props: {
        post: {
            type: Object,
            required: true,
        },
    },
    emits: [
        'delete-post',
        'edit-post',
        'pin-unpin-post',
        'report-post',
    ],
    setup() {
        const { t } = useTranslation('post');
        return { t };
    },
    data() {
        return {
            TagSize,
            menuItems: [
                {
                    icon: 'edit',
                    name: 'edit',
                    onClick: this.emitEditPost,
                    authorizedAction: true,
                },
                {
                    icon: 'pin',
                    name: 'pin',
                    onClick: this.emitPinUnpinPost,
                    authorizedAction: false,
                },
                {
                    icon: 'flag',
                    name: 'report',
                    onClick: this.emitReportPost,
                    authorizedAction: true,
                },
                {
                    icon: 'trash',
                    name: 'delete',
                    onClick: this.emitDeletePost,
                    authorizedAction: true,
                },
            ],
        };
    },
    computed: {
        ...mapGetters('ui', ['screen']),
        ...mapGetters('auth', ['isAdmin', 'isLoggedIn']),
        ...mapGetters({ currentUser: 'user/user' }),
        anonymousUser() {
            return this.post.postDetails.anonymousUser;
        },
        userPreview() {
            return this.post.postDetails.anonymousUser ? this.anonymousUser.user_preview : null;
        },
        selectedIcon() {
            return `icon-${this.context}`;
        },
        isPostOwner() {
            return this.post.isOwner;
        },
        conditionalMenuItems() {
            return this.menuItems
                .filter((items) => items.authorizedAction === true)
                .map((item) => ({
                    ...item,
                    label: this.t(`post:menu:${item.name}`),
                    class: item.name === 'reported' ? 'text-red-400 pointer-events-none cursor-default' : '',
                }));
        },
        context() {
            return this.post.postDetails.context;
        },
        contextData() {
            return this.post[this.context];
        },
        shareUrl() {
            const userID = this.isLoggedIn ? this.currentUser.userid : null;
            return `${this.post.postDetails.url}?ref=${userID}`;
        },
        urlToContextPage() {
            switch (this.context) {
                case 'course':
                    return route('course.show', {
                        courseName: this.contextData.name,
                        id: this.contextData.id,
                    }).toString();
                case 'document':
                    return this.contextData.link;
                case 'group':
                    return route('groups.show', {
                        groupName: this.contextData.name,
                        id: this.contextData.id,
                    }).toString();
                default:
                    return '';
            }
        },
        shouldShowContextData() {
            return route().current('newsfeed.show') || (route().current('course.show') && this.isDocumentContext);
        },
        shouldShowExtraTag() {
            return route().current('newsfeed.show') && this.isDocumentContext;
        },
        isDocumentContext() {
            return this.context === 'document';
        },
        hasPinnedPost() {
            return this.$store.getters[`${this.context}/hasPinnedPost`];
        },
        user() {
            return this.post.postDetails.user;
        },
        reportedState() {
            const reportedState = this.post.postDetails.isReported && this.post.postDetails.userReport;
            return reportedState;
        },
        canPinPost() {
            if (['course.show', 'groups.show'].indexOf(route().current()) === -1) {
                return false;
            }
            if (!this.isAdmin) {
                return false;
            }
            if (this.hasPinnedPost && !this.post.isPinned) {
                return false;
            }
            return true;
        },
    },

    watch: {
        reportedState(newVal) {
            if (newVal) {
                this.setReported();
            }
        },
    },

    beforeMount() {
        this.setAuthorizedActions();
        this.setReported();
    },

    methods: {
        route,
        setReported() {
            if (this.reportedState) {
                this.menuItems[2].name = 'reported';
            }
        },
        setAuthorizedActions() {
            if (this.isLoggedIn) {
                // edit Post
                this.menuItems[0].authorizedAction = this.isPostOwner || this.isAdmin;

                // pin Post
                this.menuItems[1].authorizedAction = this.canPinPost;

                if (this.post.isPinned) {
                    this.menuItems[1].name = 'unpin';
                }

                // report Post
                this.menuItems[2].authorizedAction = !this.isPostOwner;

                // delete Post
                this.menuItems[3].authorizedAction = this.isPostOwner || this.isAdmin;
            }
        },
        emitDeletePost() {
            this.$emit('delete-post');
        },
        emitEditPost() {
            this.$emit('edit-post');
        },
        emitPinUnpinPost() {
            this.$emit('pin-unpin-post');
        },
        stripWhiteSpaceSpecialCharacters,
        emitReportPost() {
            this.$emit('report-post');
        },
    },
};
</script>
<style lang="scss" scoped>
.tag {
    @apply truncate;

    &.restricted {
        max-width: 66%;
    }
}
</style>
